import axios, { AxiosResponse } from "axios"
import { UpdateActivoImpuestoEmpresaCommandProps } from "./Commands/UpdateActivoImpuestoEmpresaCommand"
import { GetImpuestoEmpresaByIdDtoProps } from "./DTOs/GetImpuestoEmpresaByIdDto"
import { ImpuestoEmpresaCreationDtoProps } from "./DTOs/ImpuestoEmpresaCreationDto"
import { ImpuestoEmpresaReadingDtoProps } from "./DTOs/ImpuestoEmpresaReadingDto"
import { ImpuestoEmpresaUpdateDtoProps } from "./DTOs/ImpuestoEmpresaUpdateDto"
import {
  impuestoEmpresaByIdAdapter,
  impuestoEmpresaNewReadingDtoAdapter,
  impuestoEmpresaReadingDtoAdapter,
} from "./ImpuestoEmpresa.adapter"
import { GetAllImpuestosDtoIPagedEnumerableProps } from "./Schemas/GetAllImpuestosDtoIPagedEnumerable"
import { ImpuestoCategoriaType } from "./Types/ImpuestoCategoria"
import { TipoGrupoImpuestoType } from "./Types/TipoGrupoImpuesto"
import { TipoImpuestoEmpresaEnumType } from "./Types/TipoImpuestoEmpresaEnum"
import { SortingCriteria } from "./interfaces/SortingCriteria"

export interface ImpuestoEmpresaReadingDtoQueryParamsProps {
  EmpresaId?: string
  EsActivo?: boolean
  GrupoImpuestoId?: number
  ImpuestoCategoria?: ImpuestoCategoriaType
  IsPagingEnabled?: boolean
  PorDefecto?: boolean
  TipoGrupoImpuesto?: TipoGrupoImpuestoType
  TipoImpuestoEmpresa?: TipoImpuestoEmpresaEnumType
  Valor?: number
  SortingCriteria?: SortingCriteria
  IsEnabled?: boolean
  MaxResultCount?: number
  SkipCount?: number
}

/**
 * Devuelve una lista SIN PAGINAR basada en los parámetros de consulta proporcionados.
 * @param queryParams Los parámetros de consulta opcionales para filtrar los resultados.
 * @returns Una promesa que resuelve a un array de impuestos.
 * @deprecated Tratar de usar getImpuestoEmpresaNew y colaborar en la eliminación de este servicio sin paginar.
 */
export async function getImpuestoEmpresa(
  queryParams?: ImpuestoEmpresaReadingDtoQueryParamsProps
): Promise<Array<ImpuestoEmpresaReadingDtoProps>> {
  const {
    data,
  }: Record<"data", Array<ImpuestoEmpresaReadingDtoProps>> = await axios.get(
    "/ImpuestoEmpresa/all-by-filters",
    {
      params: queryParams,
    }
  )
  return data.map((impuesto) => impuestoEmpresaReadingDtoAdapter(impuesto))
}

/**
 * Devuelve una lista PAGINADA basada en los parámetros de consulta proporcionados.
 * @param queryParams Los parámetros de consulta opcionales para filtrar los resultados.
 * @returns Una promesa que resuelve a un objeto que contiene la lista paginada de registros de ImpuestoEmpresa.
 */
export async function getImpuestoEmpresaNew(
  queryParams?: ImpuestoEmpresaReadingDtoQueryParamsProps
): Promise<GetAllImpuestosDtoIPagedEnumerableProps> {
  const { data }: Record<"data", GetAllImpuestosDtoIPagedEnumerableProps> =
    await axios.get("/ImpuestoEmpresa", {
      params: queryParams,
    })
  return {
    currentPage: data.currentPage,
    items: data.items?.map((impuesto) =>
      impuestoEmpresaNewReadingDtoAdapter(impuesto)
    ),
    totalCount: data.totalCount,
  }
}

/**
 * Updates an ImpuestoEmpresa record.
 * @param impuestoEmpresaBody - The ImpuestoEmpresa object to be updated.
 * @returns A Promise that resolves to the response from the server.
 */
export async function putImpuestoEmpresa(
  impuestoEmpresaBody: ImpuestoEmpresaUpdateDtoProps
): Promise<AxiosResponse> {
  const response = await axios.put("/ImpuestoEmpresa", impuestoEmpresaBody)

  return response
}

/**
 * Retrieves an ImpuestoEmpresa by its ID.
 * @param id - The ID of the ImpuestoEmpresa to retrieve.
 * @returns A Promise that resolves to a GetImpuestoEmpresaByIdDtoProps object.
 */
export async function getImpuestoEmpresaById(
  id: string
): Promise<GetImpuestoEmpresaByIdDtoProps> {
  const { data } = await axios.get(`/ImpuestoEmpresa/${id}`)

  return impuestoEmpresaByIdAdapter(data)
}

/**
 * Activates an ImpuestoEmpresa.
 *
 * @param activarImpuestoEmpresaBody - The body of the request containing the properties to update the ImpuestoEmpresa.
 * @returns A Promise that resolves to the AxiosResponse object.
 */
export async function activarImpuestoEmpresa(
  activarImpuestoEmpresaBody: UpdateActivoImpuestoEmpresaCommandProps
): Promise<AxiosResponse> {
  const response = await axios.put(
    `/ImpuestoEmpresa/${activarImpuestoEmpresaBody.id}/activar`,
    activarImpuestoEmpresaBody
  )

  return response
}

export async function postImpuestoEmpresa(
  requestBody: ImpuestoEmpresaCreationDtoProps
): Promise<AxiosResponse> {
  const response = await axios.post("/ImpuestoEmpresa", requestBody)

  return response
}
